import { Fragment, type ReactNode, type CSSProperties } from "react";
import { TooltipContainer } from "@outplayed/tooltips";
import { getClassicItemIconUrl, type ClassicItem } from "@ugg/shared/hooks/classic";

// Part of the League Classic build page. See ./ClassicChampionProfile.tsx for
// the module boundary rules: nothing outside this folder imports from here
// except ./index.ts, and nothing in here is hoisted into shared component
// directories — that is what keeps Classic deletable as one unit.

const ITEM_TAG_STYLE: Record<string, CSSProperties> = {
  attention: { color: "#ffffff", fontWeight: 700 },
  jadeUnique: { color: "#e0b354", fontWeight: 700 },
  keyword: { color: "#f0e6d2" },
  keywordMajor: { color: "#f0e6d2", fontWeight: 700 },
  gold: { color: "#e0b354" },
  danger: { color: "#e0524f" },
  jadeLimit: { color: "#e0524f" },
  flavorText: { color: "#8b8ba7", fontStyle: "italic" },
  jadeRules: { color: "#8b8ba7" },
  rules: { color: "#8b8ba7" },
  magicDamage: { color: "#31c8e6" },
  physicalDamage: { color: "#ff9b6b" },
  trueDamage: { color: "#e0c56e" },
  scaleAP: { color: "#31c8e6" },
  scaleAD: { color: "#ff9b6b" },
  scaleHealth: { color: "#8fce5b" },
  scaleMana: { color: "#5db6ff" },
  scaleArmor: { color: "#f0b072" },
  scaleMR: { color: "#8fd6ff" },
  healing: { color: "#4bd15a" },
  lifeSteal: { color: "#4bd15a" },
  spellVamp: { color: "#4bd15a" },
  shield: { color: "#c9c9d6" },
  speed: { color: "#e0a458" },
  status: { color: "#ffffff", fontWeight: 700 },
  spellName: { color: "#d5a353" },
  armorPen: { color: "#ff9b6b" },
  magicPen: { color: "#31c8e6" },
  attackSpeed: { color: "#e0a458" },
  scaleCrit: { color: "#ff9b6b" },
  abilityHaste: { color: "#31c8e6" },
};

// Render an item's HTML description (custom tags + <br>) into styled nodes,
// using the top-of-stack tag style for each text run.
function renderItemDescription(html: string | undefined): ReactNode {
  if (typeof html !== "string" || !html) return null;
  const tokens = html.split(/(<br\s*\/?>|<\/?[a-zA-Z][a-zA-Z0-9]*>)/g);
  const styleStack: Array<CSSProperties | undefined> = [];
  const nodes: ReactNode[] = [];
  let key = 0;
  for (const tok of tokens) {
    if (!tok) continue;
    if (/^<br\s*\/?>$/.test(tok)) {
      nodes.push(<br key={key++} />);
      continue;
    }
    const open = tok.match(/^<([a-zA-Z][a-zA-Z0-9]*)>$/);
    if (open) {
      styleStack.push(ITEM_TAG_STYLE[open[1]]);
      continue;
    }
    if (/^<\/[a-zA-Z][a-zA-Z0-9]*>$/.test(tok)) {
      styleStack.pop();
      continue;
    }
    const style = styleStack[styleStack.length - 1];
    nodes.push(
      style ? (
        <span key={key++} style={style}>
          {tok}
        </span>
      ) : (
        <Fragment key={key++}>{tok}</Fragment>
      ),
    );
  }
  return <>{nodes}</>;
}

// Item detail body (name + price header, divider, styled description). Used both
// for the hover tooltip and the Items Summary cards (which show their own icon
// alongside). No icon here, matching the rest of the site's tooltips.
function ClassicItemDetails(props: { item: ClassicItem }) {
  const { item } = props;
  return (
    <div className="max-w-[320px] text-[13px] leading-[18px]">
      <div className="flex items-start justify-between gap-[12px]">
        <span className="min-w-0 font-bold text-white">{item.name}</span>
        {item.priceTotal > 0 && <span className="flex-none font-bold text-[#e0b354]">{item.priceTotal}g</span>}
      </div>
      <div className="my-[8px] border-t border-purple-100" />
      <div className="text-lavender-50">{renderItemDescription(item.description)}</div>
    </div>
  );
}

// A build item: icon (+ quantity badge for stacked consumables) with a hover
// tooltip once the full item manifest resolves. Bare icon until then.
export function ClassicItemIcon(props: { itemId: number; item?: ClassicItem; name?: string; quantity?: number }) {
  const { itemId, item, name, quantity } = props;
  const img = (
    <img
      src={getClassicItemIconUrl(itemId)}
      alt={item?.name || name || `Item ${itemId}`}
      className="flex-none rounded-[3px]"
      style={{ width: 36, height: 36 }}
    />
  );
  const content =
    quantity && quantity > 1 ? (
      // Quantity badge — same overlay treatment as the skill keybind, pinned to
      // the icon's bottom-right corner.
      <div className="relative flex-none">
        {img}
        <div className="absolute bottom-0 right-0 translate-x-1/4 translate-y-1/4 flex items-center justify-center min-w-[16px] h-[16px] px-[3px] rounded-full border-[2px] border-[#17172e] bg-[#383864] text-white text-[10px] font-bold leading-none">
          {quantity}
        </div>
      </div>
    ) : (
      img
    );
  if (!item) return content;
  return <TooltipContainer tooltipInfo={<ClassicItemDetails item={item} />}>{content}</TooltipContainer>;
}

// ─── Champion Info (replaces the Role Quest slot) ─────────────────────────────

// ─── Skills (icon + name + description per spell card) ────────────────────────

// Spell cards showing the ability icon, name and plain description. The full
// tooltip (cooldown + resolved dynamic description) is shown on icon hover,
// matching the header's skill-icon preview. Laid out 2-up: Q/W, E/R, then P.
